import isEmpty from 'lodash/isEmpty'
import type { UseListResult } from '@ztechapps/common.hook'
import { useDeepCompareEffect, useList, useQueryEffect } from '@ztechapps/common.hook'
import { shippingGetRatesQuery } from '../graphql'
import { useCheckoutContext } from '../index'
import orderBy from 'lodash/orderBy'
import groupBy from 'lodash/groupBy'
import type { CarrierRate, GroupedCarrierRate } from '../types'
import { useEffect, useMemo } from 'react'
import { useValidateShipment } from './useValidateShipment'

export const useOrderShippingRates = (): [CarrierRate[], UseListResult<CarrierRate>] => {
  const context = useCheckoutContext()
  const orderId = context.orderId
  const checkoutErrors = context.checkoutErrors
  const [shippingOptions] = context.shippingOptions

  const { validateShippingOptions } = useValidateShipment()

  //initially it should clear the checkout errors
  useEffect(() => {
    checkoutErrors.reset()
    // checkoutErrors intentionally excluded: useCheckoutErrors returns a new object (with a new
    // `reset`) every render, so including it would call reset() — a setState to a new empty array —
    // on every render, looping. Only the mount matters here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useDeepCompareEffect(() => {
    //before we even check for the shipping we need to first validate the information
    //make sure we have a valid address, date

    checkoutErrors.reset()
    const validationResult = validateShippingOptions()

    if (validationResult !== true) {
      checkoutErrors.push(validationResult as string[])
    }
  }, [shippingOptions.shipmentDate, shippingOptions.shippingAddress, shippingOptions.totalWeight])

  const filter = { orderId }
  const carrierListResult = useList<CarrierRate>({
    query: shippingGetRatesQuery,
    filter,
    limit: 0,
    options: {
      //nextFetchPolicy must stay network-only — the useList default ('cache-only') flips the
      //active policy to cache-only right after the request starts, which reads the still-empty
      //cache and emits a premature ready state (loading=false, networkStatus=7) BEFORE the rates
      //arrive. That false-loading-with-empty-list window is what made the carrier picker show
      //only the custom options on first load. Keeping it network-only holds loading=true until
      //the live rates actually land.
      queryOptions: { skip: isEmpty(orderId), fetchPolicy: 'network-only', nextFetchPolicy: 'network-only' },
    },
  })

  const carrierRates: CarrierRate[] = useMemo(() => {
    return carrierListResult?.list ?? []
  }, [carrierListResult.list])

  useQueryEffect(() => {
    const errorMessage = carrierListResult?.error?.message
    if (errorMessage) {
      // let parsedMessage = parseErrors(errorMessage)
      // parsedMessage = isEmpty(parsedMessage) ? errorMessage : parsedMessage
      checkoutErrors.push(errorMessage) //show the actual error message
    }
  }, carrierListResult)

  return [carrierRates, carrierListResult]
}

export const useSortedCarrierRate = (): [CarrierRate[], UseListResult<CarrierRate>] => {
  const [carrierRates, carrierListResult] = useOrderShippingRates()

  const sortedCarrierRate: CarrierRate[] = orderBy(carrierRates, ['carrierName', 'shippingAmount'], ['desc', 'asc'])

  return [sortedCarrierRate, carrierListResult]
}

export const useGroupedCarrierRate = (): [GroupedCarrierRate, UseListResult<CarrierRate>] => {
  //not loading anymore so we need to group the list
  const [sortedCarrierRate, carrierListResult] = useSortedCarrierRate()

  const group = groupBy(sortedCarrierRate, (item) => [item.carrierName]) as GroupedCarrierRate

  return [group, carrierListResult]
}
